import { useEffect, useImperativeHandle, useRef, forwardRef, useState } from "react";
import { RotateCcw, Check } from "lucide-react";

/**
 * Simple signature pad using a canvas element.
 * exposes .clear() and .getPNG() via ref.
 */
export const SignaturePad = forwardRef(function SignaturePad({ onDone, height = 180 }, ref) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#e5e7eb";
  }, [height]);

  const point = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    setEmpty(false);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = point(e);
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = point(e);
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
  };

  const getPNG = () => new Promise((resolve) => {
    canvasRef.current.toBlob((blob) => resolve(blob), "image/png");
  });

  useImperativeHandle(ref, () => ({ clear, getPNG, isEmpty: () => empty }));

  return (
    <div className="rounded-md border border-dashed border-border/70 bg-secondary/30 p-2">
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="block w-full touch-none"
        style={{ height }}
      />
      <div className="mt-2 flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">{empty ? "Sign above" : "Signed"}</p>
        <div className="flex gap-2">
          <button
            type="button" data-testid="signature-clear" onClick={clear}
            className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-[11px]"
          >
            <RotateCcw className="h-3 w-3" /> Clear
          </button>
          {onDone && (
            <button
              type="button" data-testid="signature-done"
              onClick={async () => onDone(await getPNG())}
              disabled={empty}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground disabled:opacity-50"
            >
              <Check className="h-3 w-3" /> Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
